/**
 * RequestsPage
 *
 * Renders a paginated, filterable list of approval requests with inline actions
 * (Approve/Reject) and a per-row chat panel. Integrates with the shared MUI table
 * wrapper and exposes callbacks to update status and comments. Uses the global
 * loading overlay via useSetLoading.
 */
import React, { useEffect, useMemo, useState } from "react";
import Status from "./status/status";
import { AlertComponent } from "../../../common/components";
import { TableHeader } from "../../../common/components/mui-table/mui-table";
import {
  addRequestComment,
  getRequest,
  getRequestCommentsById,
  updateRequestStatus,
} from "../../../api/request";
import { useSetLoading } from "../../../common/hooks/appearance";
import greenCircleIcon from "../../../assets/icons/greenCircleIcon.svg";
import redCircleIcon from "../../../assets/icons/redCircleIcon.svg";
import RejectRequestPopup from "../../../common/components/reject-request-popup/reject-request-popup";
import "./request.scss";
import dayjs from "dayjs";
import { Avatar, Drawer, Stack } from "@mui/material";
import UserDashboardAllObjectiveDrawer from "../../user-dashboard/user-dashboard-all-objective-drawer/user-dashboard-all-objective-drawer";
import { CommentType } from "../../../common/components/chat-popover/comment";
import MUITableRequest from "../../../common/components/mui-table/mui-table-request";

/** Column configuration for the requests table. */
const headers: TableHeader[] = [
  { key: "requestType", label: "Request Type" },
  { key: "title", label: "Title" },
  { key: "linkedTo", label: "Linked to" },
  { key: "approver", label: "Approver" },
  { key: "status", label: "Status", filterable: true },
  { key: "date", label: "Date" },
  { key: "requester", label: "Requester" },
];

/**
 * Transforms a nested API comment payload into the UI CommentType the chat
 * component expects. Preserves replies and inlines simple file attachments.
 */
const transformCommentData = (apiComment: any): CommentType => {
  let html = apiComment.comment;
  if (apiComment.filePathJson) {
    try {
      const jsonString =
        typeof apiComment.filePathJson === "string"
          ? apiComment.filePathJson
          : JSON.stringify(apiComment.filePathJson);

      const filePaths = JSON.parse(jsonString); // validate
      let fileHtml = "";
      if (Array.isArray(filePaths)) {
        filePaths.forEach((filePath) => {
          const isImage = ["jpg", "jpeg", "png", "gif"].some((ext) =>
            filePath.toLowerCase().endsWith(ext),
          );
          if (isImage) {
            fileHtml += `<br><img src="${filePath}" alt="attachment" style="max-width: 200px; max-height: 200px;"/>`;
          } else {
            fileHtml += `<br><a href="${filePath}" target="_blank" rel="noopener noreferrer">${filePath}</a>`;
          }
        });
      }
      html += fileHtml;
    } catch (e) {
      console.error(e);
    }
  }

  return {
    id: apiComment.commentId.toString(),
    author: {
      id: apiComment.authorName,
      name: apiComment.authorName,
    },
    html: html,
    timestamp: apiComment.createdAt,
    replies: apiComment.replies
      ? apiComment.replies.map(transformCommentData)
      : [],
  };
};

const RequestsPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<any>({
    status: [],
  });
  const [selectedMenuOption, setSelectedMenuOption] = useState(null);
  const [rejectPopupOpen, setRejectPopupOpen] = useState(false);
  const [rejectId, setRejectId] = useState<number | null>(null);
  const [isShowUserObjectiveDrawer, setIsShowUserObjectiveDrawer] =
    useState(false);
  const [selectedApprover, setSelectedApprover] = useState<any>(null);
  const [chatComments, setChatComments] = useState<Record<number, any[]>>({});
  const [activeChatRow, setActiveChatRow] = useState<number | null>(null);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const ALL_REQUEST_STATUSES = ["pending", "approved", "rejected"];

  /** Merge new filters and ensure empty status means "all statuses". */
  const handleFilterChange = (newFilters: any) => {
    setActiveFilters((prev: any) => {
      const next = { ...prev, ...newFilters };
      // If status is explicitly cleared, send all statuses so API returns everything
      if (Object.hasOwn(newFilters, "status")) {
        const s = newFilters.status;
        if (s === undefined || (Array.isArray(s) && s.length === 0)) {
          next.status = ALL_REQUEST_STATUSES;
        }
      }
      return next;
    });
    setCurrentPage(1);
  };

  /** Approve/Reject a request and refresh the current page. */
  const handleUpdateStatus = async (
    titleId: number,
    status: "approve" | "reject",
  ) => {
    try {
      const backendStatus = status === "approve" ? "Approved" : "Rejected";
      await updateRequestStatus(titleId, backendStatus);
      fetchRequests(activeFilters);
      setAlertMessage(`Request ${status}d successfully!`);
      setAlertType("success");
      setAlertOpen(true);
    } catch (err) {
      console.error(err);
      setAlertMessage(`Failed to ${status} request`);
      setAlertType("error");
      setAlertOpen(true);
    }
    setRejectPopupOpen(false);
  };

  // Stack is optional for layout

  /** Fetch table rows with optional filters and project into the table row shape. */
  const fetchRequests = async (filters: any = {}) => {
    setLoading(true);
    try {
      // Clean filters: drop undefined/null and empty arrays so server doesn't apply defaults unexpectedly
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) =>
          Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null,
        ),
      );

      const response = await getRequest(cleanFilters);

      const updatedData = response.data.data.map((item: any) => {
        const approverName = item.approver || "";
        const approverImage = item.approverImage || null;

        const getInitials = (name: string) => {
          if (!name) return "";
          const parts = name.trim().split(" ");
          if (parts.length === 1) return parts[0][0]?.toUpperCase();
          return (parts[0][0] + parts.at(-1)![0])?.toUpperCase();
        };

        return {
          requestType: item.requestType || "_",
          title: item.titleName || "",
          linkedTo: item.linkedTo || "_",
          id: item.referenceId || "",
          approver: (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row expansion
                setSelectedApprover({
                  id: item.approverId, // assuming you have approverId
                  name: approverName,
                });
                setIsShowUserObjectiveDrawer(true);
              }}
              style={{ cursor: "pointer" }}
            >
              {approverImage ? (
                <Avatar src={approverImage} alt={approverName} />
              ) : (
                <Avatar>{getInitials(approverName)}</Avatar>
              )}
            </Stack>
          ),
          status: (
            <Status
              status={
                item.status
                  ? item.status.charAt(0).toUpperCase() +
                    item.status.slice(1).toLowerCase()
                  : "-"
              }
            />
          ),
          date: item.date ? dayjs(item.date).format("MM-DD-YYYY") : "",
          requester: item.requester || "_",
          titleId: item.titleId,
          rawStatus: item.status?.toLowerCase() || "pending", // for filtering
          message: item?.message,
          // Fields for expandable row
          fieldValue: item?.message,
          oldValue: item.oldData,
          newValue: item.newData,
        };
      });

      setData(updatedData);
      setLoading(false);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch requests",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  /** Lazily load comments for the selected row and open the chat panel. */
  const handleOpenChat = async (rowId: number | null) => {
    setActiveChatRow(rowId);
    if (rowId === null) return;

    if (!chatComments[rowId]) {
      setLoading(true);
      try {
        const res = await getRequestCommentsById(rowId);
        const transformedComments = (res.data.data.comments || []).map(
          transformCommentData,
        );
        setChatComments((prev) => ({ ...prev, [rowId]: transformedComments }));
      } catch {
        setChatComments((prev) => ({ ...prev, [rowId]: [] }));
      } finally {
        setLoading(false);
      }
    }
  };

  /** Submit a new comment (and attachments) then refresh the thread for the row. */
  const handleCommentSubmit = async (
    rowId: number,
    html: string,
    parentId: string | null,
    attachments: string[],
  ) => {
    setLoading(true);
    try {
      const data = {
        okrApprovalMapId: rowId,
        comment: html,
        parentCommentId: parentId,
        tagJson: {},
        filePathJson: JSON.stringify(attachments),
        createdBy: null,
      };
      await addRequestComment(data);
      const res = await getRequestCommentsById(rowId);
      const transformedComments = (res.data.data.comments || []).map(
        transformCommentData,
      );
      setChatComments((prev) => ({ ...prev, [rowId]: transformedComments }));
    } catch {
      setAlertMessage("Failed to add comment");
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeFilters.status.length) {
      fetchRequests({
        status: activeFilters.status, // array of statuses
      });
    }
  }, [currentPage, activeFilters.status]);

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  /** Toggle expanded row details. */
  const handleRowClick = (row: any) => {
    // Toggle expanded row
    setExpandedRow((prev) => (prev === row.titleId ? null : row.titleId));
  };

  return (
    <div className="request-page-container">
      <MUITableRequest
        columnNames={headers}
        rowsData={data}
        showChatColumn={true}
        showActionsColumnWithOptions={true}
        isRequest={true}
        onRowClick={handleRowClick}
        expandedRow={expandedRow}
        menuOptions={[
          {
            label: "Approve",
            onClick: (closeMenu: () => void, row: any) => {
              handleUpdateStatus(row.titleId, "approve");
              closeMenu();
            },
            icon: greenCircleIcon,
          },
          {
            label: "Reject",
            onClick: (closeMenu: () => void, row: any) => {
              setRejectId(row.titleId);
              setRejectPopupOpen(true);
              closeMenu();
            },
            icon: redCircleIcon,
          },
        ]}
        selectedMenuOption={selectedMenuOption}
        setSelectedMenuOption={setSelectedMenuOption}
        onFilterChange={handleFilterChange}
        onOpenChat={handleOpenChat}
        commentsByRow={chatComments}
        activeChatRow={activeChatRow}
        onCommentSubmit={handleCommentSubmit}
        canWrite={true}
        noRecordsTitle="No Requests available at the moment."
        noRecordsMessage="Please reload the page, or verify the applied filters."
        isShowMessageLoading={loading}
      />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
      <RejectRequestPopup
        isOpen={rejectPopupOpen}
        onClose={() => setRejectPopupOpen(false)}
        rejectId={rejectId}
        label="request"
        handleReject={(id: number) => handleUpdateStatus(id, "reject")}
      />
      <Drawer
        anchor={"bottom"}
        open={isShowUserObjectiveDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          if (reason === "escapeKeyDown") {
            return;
          }
          setIsShowUserObjectiveDrawer(false);
        }}
        slotProps={{
          paper: {
            sx: {
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              top: "auto", // reset default
              marginTop: "10px", // 👈 space from top
              minHeight: "91.9vh",
              maxHeight: "91.9vh",
            },
          },
        }}
      >
        <UserDashboardAllObjectiveDrawer
          onClose={() => setIsShowUserObjectiveDrawer(false)}
          approverId={selectedApprover?.id}
          setIsShowAllObjectiveDrawer={setIsShowUserObjectiveDrawer}
          // Pass whatever props you need for user-specific dashboard
        />
      </Drawer>
    </div>
  );
};

export default RequestsPage;
